import React, { useMemo } from "react";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import store from "services/store";
import createFormActions from "modules/form/actions";
import Validator from "services/validator";
import { isValidVariable } from "services/validator/rules";
import {
  extractVariablesFromProfiles,
  getVariableDetails,
} from "utils/variablesHelpers";
import { createForm } from "modules/form";
import { VariablesForm } from "modules/profileStack/blocks";
// TO DO: extract these variables from the profileStack module
import {
  getGroupedVariables,
  getProfilesWithErrors,
  getVariablesFormPayload,
} from "modules/profileStack/selectors";
import { createSelector } from "reselect";

export default function VariablesModule({
  name,
  selectors: { getProfiles },
  onSubmit,
} = {}) {
  const validator = new Validator();
  validator.addRule("variables", function* (value, key, data) {
    const profiles = getProfiles(store.getState());
    const profileVariables = extractVariablesFromProfiles(profiles);

    for (const key of Object.keys(data.variables)) {
      yield {
        field: `variables.${key}.value`,
        result: isValidVariable({
          ...getVariableDetails(data.variables[key], profileVariables),
        })(data.variables[key]),
      };
    }
  });

  const formActions = createFormActions({
    validator,
    init: () => {
      const profiles = getProfiles(store.getState());
      const variables = getVariablesFormPayload({}, profiles);
      return Promise.resolve({
        variables,
      });
    },
    submit: async (data) => {
      return await onSubmit(data);
    },
  });

  const FormComponent = createForm({
    Component: VariablesForm,
    actions: formActions,
  });

  function VariablesEditor() {
    const profiles = useSelector(getProfiles);
    const [expanded, setExpanded] = useState();
    const errors = useSelector((state) => state.forms?.[name]?.errors || []);

    const groupedVariables = useMemo(
      () => getGroupedVariables(profiles),
      [profiles]
    );

    useEffect(() => {
      setExpanded(groupedVariables.map((variable) => variable?.profile?.uid));
    }, [groupedVariables]);

    const profilesWithErrors = useMemo(() => {
      if (!errors?.length) {
        return [];
      }
      return getProfilesWithErrors(groupedVariables, errors);
    }, [groupedVariables, errors]);

    return (
      <FormComponent
        module={name}
        preventInitOnMount
        profilesWithErrors={profilesWithErrors}
        groupedVariables={groupedVariables}
        expanded={expanded}
        setExpanded={setExpanded}
      />
    );
  }

  const hasErrors = createSelector(
    (state) => state.forms?.[name]?.errors,
    (errors) => {
      return errors?.length > 0;
    }
  );

  const initialize = () => {
    store.dispatch(formActions.init({ module: name }));
  };

  const submit = async () => {
    return await store.dispatch(formActions.submit({ module: name }));
  };

  return {
    Blocks: {
      VariablesEditor,
    },
    actions: { initialize, submit },
    selectors: {
      hasErrors,
    },
  };
}
